import React from "react"
import styled, { css } from "styled-components"
import { AnimatedInputLabel } from "../../general-styles.component"
import SelectProps from "./select.interface"
import { uniqueId } from "lodash"
import { AppFunctionComponent } from "../../../../types"
import useTranslationHook from "../../../../hooks/use-translation.hook"

export const selectHeight = 60

const placeholderStyles = css`
  color: transparent;
`

const selectHiddenStyles = css`
  color: transparent;
  border: solid 1px transparent;

  &:focus {
    opacity: 0;
  }
  option {
    color: ${({ theme }) => theme.color.text};
    &:focus {
      color: transparent;
    }
  }
`

const SelectWrapper = styled.div`
  display: block;
  position: relative;
  z-index: 1;
  height: ${selectHeight}px;
`

export const SelectArrowIcon = styled.div`
  display: flex;
  position: absolute;
  justify-content: center;
  align-items: center;

  top: 9px;
  right: 0;
  width: 45px;
  height: calc(100% - 18px);
  z-index: 2;

  font-size: 1.2rem;

  cursor: pointer;
  pointer-events: none;
`

type Props = {
  className?: string
}

export const DropdownArrow: AppFunctionComponent<Props> = ({ className }) => (
  <SelectArrowIcon className={className}>&#9662;</SelectArrowIcon>
)

const SelectArrow = styled(DropdownArrow)`
  border-left: solid 1px ${({ theme }) => theme.color.commerceBorder};
  z-index: 11;
`

export const selectBoxStyles = css`
  border-radius: ${({ theme }) => theme.size.borderRadiusSmall};
  border: 1px solid ${({ theme }) => theme.color.commerceBorder};
  background-color: transparent;
`

export const selectStyles = css`
  box-sizing: border-box;

  width: 100%;
  padding: 20px 45px 8px 20px;

  font-size: 1rem;
  font-weight: 300;
  line-height: 2em;
  font-family: ${({ theme }) => theme.font.basic};

  appearance: none;
  &::-ms-expand {
    display: none;
  }
  &:-moz-focusring {
    color: transparent;
    text-shadow: 0 0 0 #000;
  }
  &:disabled {
    background-color: ${({ theme }) => theme.color.inputDisabledBackground};
    color: ${({ theme }) => theme.color.textDisabled};
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    padding-left: 15px;
  }
`

const SelectElement = styled.select<{
  placeholderVisible: boolean
  hide?: boolean
  error?: boolean
  value?: SelectProps["value"]
}>`
  ${selectBoxStyles};
  ${selectStyles};

  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  option {
    color: ${({ theme }) => theme.color.text};
    background: ${({ theme }) => theme.color.backgroundInverted};
  }

  ${({ placeholderVisible }) => placeholderVisible && placeholderStyles};
  ${({ hide }) => hide && selectHiddenStyles};

  border: 1px solid
    ${({ theme, error }) =>
      error ? theme.color.error : theme.color.commerceBorder};

  ~ ${SelectArrow} {
    ${({ theme, error }) =>
      error &&
      css`
        border-color: ${theme.color.error};
      `}

    &:disabled {
      color: ${({ theme, value }) =>
        value ? theme.color.textDisabled : "transparent"};
    }

    & + select {
      position: absolute;
      z-index: -1;
      top: 0;
      left: 0;
      border-color: transparent;
    }

    &:focus {
      border-color: ${({ theme }) => theme.color.buttonActive};
      outline: none;
      ~ ${SelectArrow}, & + select {
        border-color: ${({ theme }) => theme.color.buttonActive};
      }
    }
  }
`

const Select: AppFunctionComponent<SelectProps> = ({
  className,
  placeholder,
  value,
  options,
  required,
  showValues,
  onChange,
  error,
  selectRef,
  name,
  ...props
}) => {
  const labelId = uniqueId()
  const { translate } = useTranslationHook()
  return (
    <SelectWrapper className={className}>
      <SelectElement
        onChange={onChange}
        value={value}
        defaultValue={value}
        placeholderVisible={!value}
        hide={showValues}
        ref={selectRef}
        aria-labelledby={labelId}
        name={name}
        error={Boolean(error)}
        {...props}
      >
        {placeholder && (
          <option value={""} disabled>
            {placeholder}
          </option>
        )}
        {options &&
          Boolean(options.length) &&
          options.map((option) => (
            <option key={"option-" + option.label} {...option}>
              {option.label}
            </option>
          ))}
      </SelectElement>
      {showValues && (
        <SelectElement
          onChange={onChange}
          value={value}
          placeholder={placeholder}
          placeholderVisible={!value}
          {...props}
        >
          {placeholder && (
            <option value={""} disabled>
              {placeholder}
            </option>
          )}
          {options &&
            Boolean(options.length) &&
            options.map((option, index) => {
              const label = showValues ? `${option.value}` : option.label
              return (
                <option key={"option-" + index} {...option} label={label}>
                  {label}
                </option>
              )
            })}
        </SelectElement>
      )}
      <SelectArrow />
      <AnimatedInputLabel empty={!value} id={labelId}>
        {placeholder} {!required && `(${translate("Optional")})`}
      </AnimatedInputLabel>
    </SelectWrapper>
  )
}

export default Select
